// Helper function to create stroke color from fill color (with higher opacity)
// Fill has 0.3 opacity, stroke has 0.9 opacity
export const createStrokeColor = (fillColor: string): string => {
  // Extract rgba values from fill color
  const rgbaMatch = fillColor.match(/rgba?\((\d+),\s*(\d+),\s*(\d+)(?:,\s*([\d.]+))?\)/);

  if (rgbaMatch) {
    const r = rgbaMatch[1];
    const g = rgbaMatch[2];
    const b = rgbaMatch[3];
    // Use 0.9 opacity for stroke (higher than fill's 0.3)
    return `rgba(${r}, ${g}, ${b}, 0.9)`;
  }

  // Fallback to original color
  return fillColor;
};

// Helper function to create grayed-out color when track is not selected
export const createGrayedColor = (fillColor: string): string => {
  // Return a light gray with low opacity for non-selected tracks
  return 'rgba(200, 200, 200, 0.2)';
};

// Helper function to get unique ID from event (handles both MachineEvent and TruckEvent)
export const getEventUniqueId = (event: any): string => {
  // TruckEvent: truckId is the unique identifier for each truck event (UUID)
  // eventId is just a sequential ID, truckId is the actual unique identifier
  if ('truckId' in event && event.truckId) {
    return event.truckId;
  }
  // MachineEvent uses id (not uuid, as id is the primary identifier)
  if (event.id) {
    return event.id;
  }
  // Last resort fallback
  return event.uuid || event.eventId || '';
};

// Find all events on the same track that overlap a given time range (excluding the edited event)
export const findOverlappingEvents = (
  editedEventId: string,
  newStart: number,
  newEnd: number,
  trackEvents: { start: number; end: number; event?: any }[]
): string[] => {
  const overlappingIds: string[] = [];

  for (const seg of trackEvents) {
    if (!seg.event) continue;

    const segId = getEventUniqueId(seg.event);
    if (segId === editedEventId) continue;

    // Two ranges overlap if: newStart < seg.end && newEnd > seg.start
    if (newStart < seg.end && newEnd > seg.start) {
      overlappingIds.push(segId);
    }
  }

  return overlappingIds;
};

// Helper function to get indicator color based on change type
export const getChangeIndicatorColor = (changeType: string): string => {
  switch (changeType) {
    case 'update':
      return '#3b82f6'; // Blue for resize/update
    case 'move':
      return '#8b5cf6'; // Purple for move
    case 'delete':
      return '#ef4444'; // Red for delete
    case 'create':
      return '#10b981'; // Green for create (from split second half)
    default:
      return '#6b7280'; // Gray default
  }
};
